import * as React from 'react';

/** Material UI Packages. */
import { Typography, Tooltip, Box, Badge } from '@mui/material';
import { GridActionsCellItem } from '@mui/x-data-grid-pro';
import { styled } from '@mui/material/styles';

/** Material UI Icons. */
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditNoteIcon from '@mui/icons-material/EditNote';
import HistoryIcon from '@mui/icons-material/History';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

/** Date Format Library. */
import dayjs from 'dayjs';

/** Internal Static Assets. */
import info from 'statics/icons/info.png';

/** Internal Components. */
import { customized_style } from 'shared/config/Styles';
import { getTodoProgressSummaryColor } from 'shared/lib/todoColorHandler';
import { createQuickFilterFn } from 'shared/lib/quickFilterUtils';
import { fieldMapping } from '../../lib/shipmentFieldMapping';
import { progressBadges } from '../../lib/progressBadges';
import { getLatestStatusList } from '../../lib/statusCellUtils';

const StyledBadge = styled(Badge)(() => ({
    '& .MuiBadge-badge': {
        color: 'white',
        fontSize: '11px',
        width: '20px',
    },
}));

/** HAZ column cell (OS-574): a circle icon when any child container/alias is hazmat, blank otherwise. */
export const renderHazmatCell = (params) =>
    params.rowNode?.type === 'group' || !params.value ? (
        ''
    ) : (
        <RadioButtonUncheckedIcon titleAccess="Hazmat" sx={{ fontSize: 18, verticalAlign: 'middle' }} />
    );

const actionIconSx = (bgcolor) => ({
    fontSize: 18,
    color: 'white',
    bgcolor,
    borderRadius: '15px',
    p: '1.5px',
});

/** 헤더 텍스트 + 범례 툴팁(물음표 아이콘). */
const LegendHeader = ({ title, legend }) => (
    <div style={{ display: 'flex', alignItems: 'center' }}>
        <Typography
            sx={{
                fontWeight: 500,
                marginRight: '8px',
                fontSize: 11,
            }}
        >
            {title}
        </Typography>
        <Tooltip placement="top" title={legend}>
            <img
                src={info}
                alt="Icon"
                style={{
                    width: '18px',
                    height: '18px',
                }}
            />
        </Tooltip>
    </div>
);

/* -------------------------------------------------------------------------- */
/* actions                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Copy / Note / History 액션 컬럼.
 *
 * shipment type별 차이는 note를 찾을 때 쓰는 FK명(`mainId`)뿐이라 `fieldMapping`에서 가져온다.
 */
export const actionsCol = ({
    tabValue,
    commentList,
    handleEmailTemplateOpen,
    handleCommentOpen,
    handleHistoryOpen,
}) => {
    const { mainId } = fieldMapping[tabValue];

    return [
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 80,
            align: 'center',
            headerAlign: 'center',
            filterable: false,
            getActions: (params) => {
                const { id, row } = params;
                if (String(id).includes('auto-generated-row')) {
                    return [];
                }
                // 기존 5개 Def가 `==`와 `===`를 섞어 쓰던 자리. 느슨한 비교 쪽 동작을
                // 유지하면서 타입 불일치로 매칭이 조용히 실패하는 일이 없도록 문자열로 맞춘다.
                const hasComment = commentList.find((item) => String(item[mainId]) === String(id));

                return [
                    <Tooltip title="Copy">
                        <GridActionsCellItem
                            icon={<ContentCopyIcon sx={actionIconSx('primary.dark')} />}
                            label="Copy"
                            color="inherit"
                            onClick={() => handleEmailTemplateOpen(id, row)}
                        />
                    </Tooltip>,
                    <Tooltip title="Note">
                        <GridActionsCellItem
                            icon={<EditNoteIcon sx={actionIconSx(hasComment ? 'warning.main' : 'primary.dark')} />}
                            label="Note"
                            color="inherit"
                            onClick={() => handleCommentOpen(id, row)}
                        />
                    </Tooltip>,
                    <Tooltip title="History">
                        <GridActionsCellItem
                            icon={<HistoryIcon sx={actionIconSx('primary.dark')} />}
                            label="History"
                            color="inherit"
                            onClick={() => handleHistoryOpen(id, row)}
                        />
                    </Tooltip>,
                ];
            },
        },
    ];
};

/* -------------------------------------------------------------------------- */
/* progress                                                                    */
/* -------------------------------------------------------------------------- */

const ProgressCell = ({ params, tabValue, todoSummary, commentList }) => {
    const { mainId } = fieldMapping[tabValue];
    const badges = progressBadges[tabValue] ?? [];

    const summary = todoSummary?.[`${tabValue}:${params.row.id}`];
    if (params.rowNode.type === 'group') {
        return '';
    }

    const filteredNotes = commentList.filter(
        (n) => n[mainId] === params.row.id && n.il_type === 'note' && n.sub_id === 0,
    );

    const noteTooltip = (keys) => {
        const notes = filteredNotes.filter((n) => keys.includes(n.note_entry));
        if (!notes.length) return '';
        const allEntries = notes.flatMap((n) => [
            { ...n, isReply: false },
            ...commentList.filter((r) => r.sub_id === n.id).map((r) => ({ ...r, isReply: true })),
        ]);
        return (
            <span style={{ whiteSpace: 'pre-wrap' }}>
                {allEntries.map((n, i) => (
                    <React.Fragment key={i}>
                        {i > 0 && <br />}
                        {n.isReply && '↳ '}
                        <strong>{n.registered_by}:</strong> {n.content}
                    </React.Fragment>
                ))}
            </span>
        );
    };
    const hasNote = (keys) => filteredNotes.some((n) => keys.includes(n.note_entry));
    const badgeSx = (keys) => ({
        '& .MuiBadge-badge': {
            fontWeight: hasNote(keys),
            outline: hasNote(keys) ? '2px solid #1A237E' : 'none',
        },
    });

    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '23px',
                height: '100%',
                lineHeight: 0,
            }}
        >
            {badges.map(({ badge, noteKeys, todoKeys }) => (
                <Tooltip
                    key={badge}
                    title={noteTooltip(noteKeys)}
                    arrow
                    placement="top"
                    slotProps={{
                        tooltip: {
                            sx: { bgcolor: 'rgba(97, 97, 97, 0.96)', fontSize: '12px' },
                        },
                        arrow: { sx: { color: 'rgba(97, 97, 97, 0.96)' } },
                    }}
                >
                    <span>
                        <StyledBadge
                            badgeContent={badge}
                            color={getTodoProgressSummaryColor(summary, todoKeys ?? noteKeys)}
                            sx={badgeSx(noteKeys)}
                        />
                    </span>
                </Tooltip>
            ))}
        </div>
    );
};

/**
 * 진행 상황 뱃지 컬럼.
 *
 * 노출 뱃지·순서·범례는 `progressBadges`의 shipment type별 데이터로 결정된다.
 */
export const progressCol = ({ tabValue, todoSummary, commentList, width = 185 }) => {
    const badges = progressBadges[tabValue] ?? [];

    return [
        {
            field: 'progress',
            headerName: 'Progress',
            width,
            align: 'center',
            headerAlign: 'center',
            cellClassName: 'status-cell',
            disableExport: true,
            filterable: false,
            renderCell: (params) => (
                <ProgressCell params={params} tabValue={tabValue} todoSummary={todoSummary} commentList={commentList} />
            ),
            renderHeader: () => (
                <LegendHeader
                    title="Progress"
                    legend={
                        <>
                            {badges.map(({ badge, label }, i) => (
                                <React.Fragment key={badge}>
                                    {i > 0 && <br />}
                                    {`${badge}: ${label}`}
                                </React.Fragment>
                            ))}
                        </>
                    }
                />
            ),
        },
    ];
};

/* -------------------------------------------------------------------------- */
/* status                                                                      */
/* -------------------------------------------------------------------------- */

const StatusCell = ({ params, statusList, tabValue }) => {
    const result = getLatestStatusList(statusList, {
        parentId: params.id,
        shipmentType: tabValue,
    });

    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                backgroundColor: params.row.client_closure === 'Closed' ? '#cfe0c3' : '#ffffff',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
            }}
        >
            <Typography
                sx={{
                    fontSize: '11px',
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.3,
                }}
            >
                {result.map((item, idx) => {
                    const isZero = item.child_id === 0;
                    const date = isZero ? '' : `${dayjs(item.event_date).format('MM/DD')} `;
                    return (
                        <span key={idx} style={{ color: isZero ? 'red' : 'inherit' }}>
                            {`${date}${item.message}`}
                            <br />
                        </span>
                    );
                })}
            </Typography>
        </Box>
    );
};

/**
 * House/상태 종류별 최신 status 컬럼.
 *
 * shipment type별 차이는 `width`와 QuickFilter 적용 여부뿐이다.
 */
export const statusCol = ({ statusList, tabValue, width = 250, getApplyQuickFilterFn }) => [
    {
        field: 'status',
        headerName: 'Status',
        width,
        align: 'center',
        headerAlign: 'center',
        renderHeader: () => (
            <LegendHeader
                title="Status"
                legend={
                    <>
                        Green color indicates client closure is "Closed".
                        <br />
                        Shows the latest status for each container.
                    </>
                }
            />
        ),
        valueFormatter: (params) => {
            const result = params?.filter((item) => item !== '').join('\n') || '';
            return result;
        },
        valueGetter: (params) => {
            return params;
        },
        ...(getApplyQuickFilterFn ? { getApplyQuickFilterFn } : {}),
        renderCell: (params) => <StatusCell params={params} statusList={statusList} tabValue={tabValue} />,
    },
];

/**
 * Trucker Name lines per shipment, built once per column rebuild.
 *
 * The trucker is entered on each child row (container / HAWB alias / BOL). Lines follow the
 * order the detail panel lists the children: HBLs in store order, then each HBL's children in
 * store order. With `splitList` (Ocean Import) a container whose splits carry a trucker shows
 * the split truckers instead of its own, in grey. A child without a trucker keeps an empty
 * line so the cell stays aligned with the Container column.
 *
 * Indexed up front because the grid reads the column value for every row on each filter,
 * sort and quick-search keystroke; walking the child lists per row made that grow with
 * shipments x children.
 */
export const truckerLinesByShipment = ({
    hblList,
    hblParentKey,
    childList,
    childParentKey,
    splitList,
    splitChildKey,
}) => {
    const groupBy = (list, keyOf) => {
        const grouped = new Map();
        (list ?? []).forEach((item) => {
            const key = keyOf(item);
            if (key == null) return;
            const bucket = grouped.get(key);
            if (bucket) bucket.push(item);
            else grouped.set(key, [item]);
        });
        return grouped;
    };
    const childrenByParentId = groupBy(childList, (child) => child[childParentKey]);
    const splitsByChildId = groupBy(splitList, (split) => split[splitChildKey]);

    const linesOf = (child) => {
        const splitLines = (splitsByChildId.get(child.id) ?? [])
            .filter((split) => split.il_trade_partner_id?.name)
            .map((split) => ({ name: split.il_trade_partner_id.name, isSplit: true }));
        if (splitLines.length > 0) return splitLines;
        return [{ name: child.il_trade_partner_id?.name ?? '', isSplit: false }];
    };

    /** FTL/LTL has no HBL layer: BOLs hang off the shipment directly. */
    if (!hblList) {
        return new Map([...childrenByParentId].map(([shipmentId, bols]) => [shipmentId, bols.flatMap(linesOf)]));
    }

    const lines = new Map();
    hblList.forEach((hbl) => {
        const shipmentId = hbl[hblParentKey];
        if (shipmentId == null) return;
        const hblLines = (childrenByParentId.get(hbl.id) ?? []).flatMap(linesOf);
        const bucket = lines.get(shipmentId);
        if (bucket) bucket.push(...hblLines);
        else lines.set(shipmentId, hblLines);
    });
    return lines;
};

/**
 * Trucker Name column.
 *
 * `linesByShipmentId` (from `truckerLinesByShipment`) is the single source for the cell: the
 * column filter, sort and clipboard value come from the same lines the cell draws, so a
 * trucker hidden behind a split (or a child listed out of HBL order) can never match a
 * filter or be copied without being visible. The toolbar quick search still covers every
 * child trucker through `__quick_search`.
 */
export const truckerCol = ({ linesByShipmentId, width = 140 }) => ({
    field: 'il_trade_partner_id',
    headerName: 'Trucker Name',
    width,
    align: 'center',
    headerAlign: 'center',
    headerClassName: 'custom-scrollpanel-header',
    valueGetter: (value, row) =>
        (linesByShipmentId.get(row.id) ?? [])
            .map((line) => line.name)
            .filter(Boolean),
    valueFormatter: (value) => value?.join(', ') || '',
    renderCell: (params) => {
        const lines = linesByShipmentId.get(params.row.id) ?? [];
        if (!lines.some((line) => line.name)) return '';
        return (
            <Typography
                component="div"
                sx={{
                    ...customized_style.itemDatagrid_Typography,
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    textAlign: 'center',
                    width: '100%',
                    minWidth: 0,
                }}
            >
                {lines.map((line, index) => (
                    <Box
                        key={index}
                        title={line.name}
                        sx={{
                            color: line.isSplit ? '#6B7280' : 'inherit',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {line.name || '\u00a0'}
                    </Box>
                ))}
            </Typography>
        );
    },
    getApplyQuickFilterFn: createQuickFilterFn,
});
